<script setup>
import { ref, watch } from 'vue';
import { useElementSize } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Bubble from 'dashboard/components-next/message/bubbles/Bubble.vue';
import {
  MESSAGE_VARIANTS,
  ORIENTATION,
} from 'dashboard/components-next/message/constants';
import InternalChatComposer from './InternalChatComposer.vue';
import InternalChatMessageActions from './InternalChatMessageActions.vue';
import InternalChatMessageContent from './InternalChatMessageContent.vue';
import InternalChatEditForm from './InternalChatEditForm.vue';
import { useInternalChatMessageFocus } from './useInternalChatMessageFocus';

const props = defineProps({
  channel: { type: Object, default: null },
  saveMessage: { type: Function, required: true },
  messages: { type: Array, default: () => [] },
  currentUserId: { type: Number, default: null },
  isLoading: { type: Boolean, default: false },
  isSending: { type: Boolean, default: false },
  error: { type: String, default: '' },
  draft: { type: Object, default: null },
  typingUsers: { type: Array, default: () => [] },
  members: { type: Object, default: () => ({ users: [], teams: [] }) },
  canMentionAll: { type: Boolean, default: false },
  canManageMessages: { type: Boolean, default: false },
  canEditOwnMessages: { type: Boolean, default: true },
  messageMeta: { type: Object, default: () => ({}) },
  actionError: { type: String, default: '' },
  focusedMessageId: { type: Number, default: null },
});

const emit = defineEmits([
  'send',
  'openThread',
  'react',
  'draftChange',
  'typing',
  'delete',
  'loadOlder',
  'loadNewer',
  'back',
]);

const { t } = useI18n();
const composer = ref(null);
const editorContainer = ref(null);
const messageList = ref(null);
const { focusMessage, preserveHistory } =
  useInternalChatMessageFocus(messageList);
const { height: editorContainerHeight } = useElementSize(editorContainer);
const editingMessageId = ref(null);

watch(
  () => props.channel?.id,
  () => {
    editingMessageId.value = null;
    composer.value?.reset();
  }
);

const senderName = message =>
  message.sender?.display_name ||
  message.sender?.name ||
  t('INTERNAL_CHAT.UNKNOWN_SENDER');

const formatTime = value =>
  value
    ? new Intl.DateTimeFormat(undefined, {
        hour: '2-digit',
        minute: '2-digit',
      }).format(new Date(value))
    : '';

const send = payload => {
  emit('send', payload);
};

const startEdit = message => {
  editingMessageId.value = message.id;
};

const cancelEdit = () => {
  editingMessageId.value = null;
};

const resetComposer = payload => composer.value?.reset(payload);

defineExpose({ resetComposer, focusMessage, preserveHistory });

const reactionOptions = ['👍', '❤️', '😂', '🎉'];

const reactionForMessage = (message, emoji) =>
  (message.reactions || []).find(item => item.emoji === emoji);

const availableReactionOptions = message =>
  reactionOptions.filter(emoji => !reactionForMessage(message, emoji));

const reactionForUser = (message, emoji, userId) => {
  const reaction = reactionForMessage(message, emoji);
  if (!reaction) return null;
  const index = reaction.user_ids.indexOf(userId);
  return index === -1 ? null : reaction.reaction_ids[index];
};

const toggleReaction = (message, emoji) => {
  const reactionId = reactionForUser(message, emoji, props.currentUserId);
  emit('react', {
    messageId: message.id,
    emoji,
    reactionId,
    userId: props.currentUserId,
  });
};
</script>

<template>
  <section v-if="channel" class="flex min-w-0 flex-1 flex-col bg-n-surface-1">
    <header
      class="flex h-14 shrink-0 items-center justify-between border-b border-n-weak px-4 md:px-6"
    >
      <Button
        type="button"
        variant="ghost"
        color="slate"
        size="sm"
        icon="i-lucide-arrow-left"
        class="me-3 shrink-0 md:hidden"
        :aria-label="t('INTERNAL_CHAT.BACK_TO_CHANNELS')"
        @click="$emit('back')"
      />
      <div class="min-w-0">
        <h1 class="m-0 truncate text-sm font-medium text-n-slate-12">
          {{
            channel.name ||
            t(`INTERNAL_CHAT.CHANNEL_TYPES.${channel.channel_type}`)
          }}
        </h1>
        <p class="mb-0 text-xs text-n-slate-11">
          {{ t(`INTERNAL_CHAT.CHANNEL_TYPES.${channel.channel_type}`) }}
        </p>
      </div>
      <span class="text-xs text-n-slate-10">
        {{
          t('INTERNAL_CHAT.MESSAGE_COUNT', {
            count: channel.messages_count || 0,
          })
        }}
      </span>
    </header>
    <p
      v-if="typingUsers.length"
      class="mb-0 border-b border-n-weak px-4 py-2 text-xs text-n-slate-10 md:px-6"
      role="status"
    >
      {{
        typingUsers.length === 1
          ? t('INTERNAL_CHAT.TYPING_ONE', {
              name: typingUsers[0].display_name || typingUsers[0].name,
            })
          : t('INTERNAL_CHAT.TYPING_MANY', { count: typingUsers.length })
      }}
    </p>
    <Button
      v-if="messageMeta.has_more"
      type="button"
      variant="ghost"
      color="slate"
      size="sm"
      class="mx-4 mt-2 shrink-0 self-center md:mx-6"
      :label="t('INTERNAL_CHAT.LOAD_OLDER')"
      :disabled="isLoading"
      @click="$emit('loadOlder')"
    />
    <Button
      v-if="messageMeta.has_more_after"
      type="button"
      variant="ghost"
      color="slate"
      size="sm"
      class="mx-4 mt-2 shrink-0 self-center md:mx-6"
      :label="t('INTERNAL_CHAT.LOAD_NEWER')"
      :disabled="isLoading"
      data-testid="internal-chat-newer"
      @click="$emit('loadNewer')"
    />
    <p
      v-if="actionError"
      class="mx-4 mt-2 mb-0 text-xs text-n-ruby-11 md:mx-6"
      role="alert"
    >
      {{ actionError }}
    </p>
    <div ref="editorContainer" class="flex min-h-0 flex-1 flex-col">
      <div
        ref="messageList"
        class="flex min-h-0 flex-1 flex-col-reverse overflow-y-auto px-4 py-5 md:px-6"
        data-testid="internal-chat-message-list"
      >
        <div
          v-if="isLoading && !messages.length"
          class="m-auto text-sm text-n-slate-11"
          role="status"
        >
          {{ t('INTERNAL_CHAT.LOADING_MESSAGES') }}
        </div>
        <div
          v-else-if="!messages.length"
          class="m-auto max-w-sm text-center text-sm text-n-slate-11"
        >
          {{ t('INTERNAL_CHAT.EMPTY_MESSAGES') }}
        </div>
        <div v-else class="flex flex-col gap-2">
          <article
            v-for="message in messages"
            :key="message.id"
            :data-message-id="message.id"
            tabindex="-1"
            class="group flex gap-2"
            :class="[
              message.sender_id === currentUserId ? 'flex-row-reverse' : '',
              message.id === focusedMessageId
                ? 'rounded-lg ring-2 ring-n-brand'
                : '',
            ]"
          >
            <Avatar :name="senderName(message)" :size="24" />
            <div
              class="min-w-0 max-w-[min(calc(100%-7.25rem),32rem)] lg:max-w-[min(calc(100%-11.75rem),32rem)]"
              :class="
                message.sender_id === currentUserId ? 'items-end text-end' : ''
              "
            >
              <div
                class="mb-1 flex items-baseline gap-2 text-xs text-n-slate-10"
                :class="
                  message.sender_id === currentUserId ? 'justify-end' : ''
                "
              >
                <span class="font-medium text-n-slate-11">{{
                  senderName(message)
                }}</span>
                <time :datetime="message.created_at">{{
                  formatTime(message.created_at)
                }}</time>
                <span
                  v-if="message.edited_at"
                  class="text-[11px] text-n-slate-9"
                >
                  {{ t('INTERNAL_CHAT.EDITED') }}
                </span>
              </div>
              <Bubble
                class="relative px-4 py-3"
                :variant="
                  message.sender_id === currentUserId
                    ? MESSAGE_VARIANTS.AGENT
                    : MESSAGE_VARIANTS.USER
                "
                :orientation="
                  message.sender_id === currentUserId
                    ? ORIENTATION.RIGHT
                    : ORIENTATION.LEFT
                "
              >
                <InternalChatMessageContent :message="message">
                  <template v-if="editingMessageId === message.id" #editor>
                    <InternalChatEditForm
                      :key="message.id"
                      :message="message"
                      :save-message="saveMessage"
                      :members="members"
                      :can-mention-all="canMentionAll"
                      @close="cancelEdit"
                    />
                  </template>
                </InternalChatMessageContent>
                <div
                  v-if="!message.deleted && message.reactions?.length"
                  class="mt-2 flex flex-wrap gap-1"
                >
                  <Button
                    v-for="reaction in message.reactions"
                    :key="reaction.emoji"
                    type="button"
                    variant="faded"
                    size="xs"
                    class="rounded-full"
                    :color="
                      reactionForUser(message, reaction.emoji, currentUserId)
                        ? 'blue'
                        : 'slate'
                    "
                    :aria-label="
                      t('INTERNAL_CHAT.REACT_WITH', { emoji: reaction.emoji })
                    "
                    @click="toggleReaction(message, reaction.emoji)"
                  >
                    {{ reaction.emoji }}
                    <span class="ms-1">{{ reaction.count }}</span>
                  </Button>
                </div>
                <div
                  v-if="
                    !message.deleted &&
                    editingMessageId !== message.id &&
                    availableReactionOptions(message).length
                  "
                  class="pointer-events-none absolute top-0 z-10 w-max opacity-0 transition-opacity duration-150 group-hover:pointer-events-auto group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:opacity-100"
                  :class="
                    message.sender_id === currentUserId
                      ? 'end-full pe-2'
                      : 'start-full ps-2'
                  "
                  data-testid="internal-chat-quick-reactions"
                >
                  <div
                    class="grid grid-cols-2 gap-1 rounded-xl bg-n-alpha-3 p-1 text-n-slate-12 shadow-lg outline outline-1 outline-n-container backdrop-blur-[100px] lg:grid-cols-4"
                  >
                    <Button
                      v-for="emoji in availableReactionOptions(message)"
                      :key="emoji"
                      type="button"
                      size="xs"
                      variant="ghost"
                      color="slate"
                      :aria-label="t('INTERNAL_CHAT.REACT_WITH', { emoji })"
                      @click="toggleReaction(message, emoji)"
                    >
                      {{ emoji }}
                    </Button>
                  </div>
                </div>
              </Bubble>
              <InternalChatMessageActions
                :message="message"
                :current-user-id="currentUserId"
                :can-manage-messages="canManageMessages"
                :can-edit-own-messages="canEditOwnMessages"
                :disabled="editingMessageId === message.id"
                @edit="startEdit"
                @delete="emit('delete', $event)"
                @reply="$emit('openThread', $event)"
              />
            </div>
          </article>
        </div>
      </div>
      <InternalChatComposer
        ref="composer"
        :container-height="editorContainerHeight"
        :disabled="isSending"
        :error="error"
        :draft="draft"
        :members="members"
        :can-mention-all="canMentionAll"
        @send="send"
        @draft-change="$emit('draftChange', $event)"
        @typing="$emit('typing', $event)"
      />
    </div>
  </section>
  <section
    v-else
    data-testid="internal-chat-empty-state"
    class="flex min-w-0 flex-1 items-center justify-center bg-n-surface-1 px-6 text-center"
  >
    <div data-testid="internal-chat-empty-state-content">
      <span
        class="i-lucide-messages-square mx-auto mb-3 block size-10 text-n-slate-9"
        aria-hidden="true"
      />
      <p class="mb-0 text-sm text-n-slate-11">
        {{ t('INTERNAL_CHAT.SELECT_CHANNEL') }}
      </p>
    </div>
  </section>
</template>
